<template>
	<div
		v-if="showGuide && currentGuideStep"
		class="fixed inset-0 z-40 pointer-events-none font-sans">
		<!-- Backdrop -->
		<div
			class="absolute inset-0 bg-black/50 backdrop-blur-[2px] pointer-events-auto transition-opacity duration-300"
			@click="dismissGuide" />

		<!-- Spotlight ring over target element -->
		<div
			v-if="targetElement"
			class="absolute pointer-events-none"
			:style="spotlightStyle">
			<div
				class="absolute inset-0 rounded-xl ring-2 ring-primary-400 shadow-[0_0_0_2px_rgba(26,155,138,0.4),0_0_24px_rgba(26,155,138,0.2)]" />
		</div>

		<!-- Tooltip card -->
		<div
			class="fixed bg-surface dark:bg-dark-700 rounded-card shadow-dropdown p-6 w-full max-w-sm z-50 pointer-events-auto border border-primary-100 dark:border-dark-600 transform transition-all duration-300"
			:style="tooltipPosition">
			<!-- ── Normal step ── -->
			<template v-if="!guideComplet">
				<div class="flex items-center justify-between mb-4">
					<div class="flex items-center gap-2">
						<div
							class="w-6 h-6 rounded-lg bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center">
							<span
								class="w-1.5 h-1.5 rounded-full bg-primary-400 animate-blink" />
						</div>
						<span
							class="text-[11px] font-bold uppercase tracking-widest text-primary-600 dark:text-primary-400">
							{{ currentGuideStep.milestone?.title || "Guide" }}
						</span>
					</div>
					<button
						@click="dismissGuide"
						class="w-7 h-7 rounded-lg flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors"
						aria-label="Close guide">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2.5"
								d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<!-- Step info -->
				<div class="flex items-start gap-3 mb-5">
					<div
						class="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0 mt-0.5">
						<svg
							class="w-4 h-4 text-primary-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								:d="stepIcon" />
						</svg>
					</div>
					<div>
						<h3
							class="font-display text-[15px] font-bold text-dark-700 dark:text-white mb-1">
							{{ currentGuideStep.step?.title }}
						</h3>
						<p
							class="text-[13px] text-muted dark:text-muted-light leading-relaxed">
							{{ currentGuideStep.step?.description }}
						</p>
					</div>
				</div>

				<!-- Step counter dots -->
				<div v-if="totalSteps > 1" class="flex items-center gap-1.5 mb-4">
					<span
						v-for="n in totalSteps"
						:key="n"
						class="h-1.5 rounded-full transition-all duration-300"
						:class="
							n === currentStepIndex
								? 'w-4 bg-primary-400'
								: 'w-1.5 bg-primary-100 dark:bg-dark-500'
						" />
				</div>

				<div class="flex gap-2">
					<button
						@click="nextStep"
						class="flex-1 px-4 py-2.5 rounded-xl font-display font-bold text-[13px] bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
						Next →
					</button>
					<button
						@click="dismissGuide"
						class="px-4 py-2.5 rounded-xl font-semibold text-[13px] bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
						Skip
					</button>
				</div>
			</template>

			<!-- ── Completion ── -->
			<template v-else>
				<div class="text-center py-4">
					<div
						class="w-16 h-16 rounded-2xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center mx-auto mb-5">
						<svg
							class="w-8 h-8 text-primary-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2.5"
								d="M5 13l4 4L19 7" />
						</svg>
					</div>
					<h3
						class="font-display text-[18px] font-extrabold text-dark-700 dark:text-white mb-2">
						Congratulations! 🎉
					</h3>
					<p
						class="text-[13px] text-muted dark:text-muted-light leading-relaxed">
						Welcome aboard! You're all set and ready to go. Explore your
						dashboard, create and manage your events, and reach out to your
						clients — all in one place. Cheers!
					</p>
				</div>
			</template>

			<!-- Pointer arrow -->
			<div
				v-if="targetElement"
				class="absolute w-3 h-3 bg-surface dark:bg-dark-700 border-l border-t border-primary-100 dark:border-dark-600 transform rotate-45"
				:style="pointerStyle" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { apiDataService } from "@/services/apiDataService";

const showGuide = ref(false);
const guideData = ref(null);
const targetElement = ref(null);
const guideComplet = ref(false);
const totalSteps = ref(0);
const currentStepIndex = ref(1);

const currentGuideStep = computed(() => guideData.value?.data || null);

// ── Selector map ─────────────────────────────────────────────────────────────
// Keys match the `screen` field returned by the API.
// Add more as new screens are introduced.
const SELECTOR_MAP = {
	DASHBOARD: '[data-guide="dashboard-stats"]',
	EVENTS: '[data-guide="events-section"]',
	EVENT_CREATE: '[data-guide="event-create"]',
	EVENT_ANALYTICS: '[data-guide="event-analytics"]',
	ANALYTICS: '[data-guide="analytics-section"]',
	REVENUE: '[data-guide="revenue-section"]',
	OUTREACH: '[data-guide="outreach-section"]',
	USERS: '[data-guide="users-section"]',
	SETTINGS: '[data-guide="settings-section"]',
	SIDEBAR: '[data-guide="sidebar"]',
	HEADER: '[data-guide="header"]',
};

// ── Step icon map ─────────────────────────────────────────────────────────────
const ICON_MAP = {
	DASHBOARD:
		"M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125Z",
	EVENTS:
		"M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
	EVENT_CREATE: "M12 4v16m8-8H4",
	EVENT_ANALYTICS:
		"M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
	REVENUE:
		"M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
	OUTREACH:
		"M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
	USERS:
		"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
	SETTINGS:
		"M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
	default: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
};

const stepIcon = computed(() => {
	const screen = currentGuideStep.value?.step?.screen;
	return ICON_MAP[screen] || ICON_MAP.default;
});

// ── Target element resolution ─────────────────────────────────────────────────
const findTargetElement = () => {
	const screen = currentGuideStep.value?.step?.screen;
	if (!screen) return null;
	const selector = SELECTOR_MAP[screen];
	return selector ? document.querySelector(selector) : null;
};

watch(
	() => currentGuideStep.value,
	() => {
		targetElement.value = findTargetElement();
	},
	{ immediate: true, deep: true },
);

onMounted(() => {
	targetElement.value = findTargetElement();
	const observer = new MutationObserver(() => {
		if (!targetElement.value) targetElement.value = findTargetElement();
	});
	observer.observe(document.body, { childList: true, subtree: true });
});

// ── Spotlight geometry ────────────────────────────────────────────────────────
const spotlightStyle = computed(() => {
	if (!targetElement.value) return {};
	const rect = targetElement.value.getBoundingClientRect();
	const p = 10;
	return {
		left: `${rect.left - p}px`,
		top: `${rect.top - p}px`,
		width: `${rect.width + p * 2}px`,
		height: `${rect.height + p * 2}px`,
	};
});

const tooltipPosition = computed(() => {
	if (!targetElement.value)
		return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
	const rect = targetElement.value.getBoundingClientRect();
	const tw = 384;
	const th = 300;
	const pad = 20;
	const pos =
		window.innerHeight - rect.bottom < th + pad && rect.top > th + pad
			? "top"
			: "bottom";
	let top = pos === "bottom" ? rect.bottom + pad : rect.top - th - pad;
	let left = rect.left + rect.width / 2 - tw / 2;
	left = Math.max(pad, Math.min(left, window.innerWidth - tw - pad));
	top = Math.max(pad, Math.min(top, window.innerHeight - th - pad));
	return { top: `${top}px`, left: `${left}px` };
});

const pointerStyle = computed(() => {
	if (!targetElement.value) return {};
	const rect = targetElement.value.getBoundingClientRect();
	const tw = 384;
	const th = 300;
	const pad = 20;
	const pos =
		window.innerHeight - rect.bottom < th + pad && rect.top > th + pad
			? "top"
			: "bottom";
	return pos === "bottom"
		? { top: "-6px", left: `${tw / 2 - 6}px` }
		: { bottom: "-6px", left: `${tw / 2 - 6}px`, top: "auto" };
});

// ── API actions ───────────────────────────────────────────────────────────────
async function nextStep() {
	try {
		const res = await apiDataService.moveToNextOnboardingStep();
		if (res?.data?.Status === "IN_PROGRESS") {
			showGuide.value = true;
			currentStepIndex.value += 1;
			await fetchGuideStatus();
			setTimeout(() => {
				targetElement.value = findTargetElement();
			}, 100);
		} else {
			guideComplet.value = true;
			setTimeout(() => {
				showGuide.value = false;
			}, 4000);
		}
	} catch (error) {
		console.error("Error moving to next step:", error);
	}
}

async function dismissGuide() {
	try {
		await apiDataService.skipOnboarding();
	} catch (error) {
		console.error("Error skipping onboarding:", error);
	} finally {
		showGuide.value = false;
	}
}

async function fetchGuideStatus() {
	try {
		const response = await apiDataService.getCurrentUserState();
		guideData.value = response;
		if (response?.data?.status === "IN_PROGRESS") {
			showGuide.value = true;
			if (response?.data?.totalSteps)
				totalSteps.value = response.data.totalSteps;
			setTimeout(() => {
				targetElement.value = findTargetElement();
			}, 300);
		}
	} catch (error) {
		console.error("Error fetching guide status:", error);
	}
}

watch(targetElement, (el) => {
	if (el)
		setTimeout(() => {
			el.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 300);
});

onMounted(() => {
	fetchGuideStatus();
	window.addEventListener("resize", () => {
		targetElement.value = findTargetElement();
	});
});
</script>
